/* eslint-disable no-empty */
import { memo, useEffect, useState } from "react";

import Clock from "@zenith/assets/icons/Clock";
import CloseCircle from "@zenith/assets/icons/CloseCircle";
import {
  SpinnerComponent,
  TextFieldComponent,
  ModalComponent,
  ToastSnackbarComponent,
} from "@zenith/components";
import { setNationalIdentificationNumberValidations } from "@zenith/features/Tiers/validations/NationalIdentificationNumberValidations";
import { AppNavigation, AppStepper, useNinVerificationPolling, TierStepper } from "@zenith/hooks";
import { UPGRADE_ACCOUNT } from "@zenith/routes/paths";
import {
  ApiResponseDTODiscardUpgradeResponseDTO,
  ApiResponseDTONINVerificationResponseDTO,
  NINVerificationRequestDTO,
  NINVerificationResponseDTO,
  TierApiService,
  TierStepsResponseDTO,
  UsersApiService,
} from "@zenith/services/user";
import { useNotificationContext, useUpgradeContext } from "@zenith/context";
import { FormDataFields, FormErrors } from "./interfaces";
import { CONNECTION_ERROR_MESSAGE } from "@zenith/data/constants";
import { getErrorTitle } from "@zenith/utils/Helpers/getAPIErrorTitle";
import CheckCircle from "@zenith/assets/icons/CheckCircle";

/* eslint-disable-next-line */
export interface NinVerificationPageProps {
  step: TierStepsResponseDTO;
}

const NinVerificationPage = memo(function NinVerificationPage(
  props: NinVerificationPageProps
) {
  const { navigateTo } = AppNavigation();
  const { setActiveStep } = AppStepper();
  const { goToNextOpenStep } = TierStepper();
  const { upgradeContext, setUpgradeContext, clearUpgradeContext } =
    useUpgradeContext();

  const ninInitialStatus = upgradeContext.isRecoveryFlow
    ? upgradeContext?.tierSteps?.find(
        (tierStep) =>
          tierStep.stepName ===
          TierStepsResponseDTO.stepName.NIN_VERIFICATION_STEP
      )?.stepStatus
    : undefined;

  const [backModal, setBackModal] = useState<boolean>(false),
    [isContinueDisabled, setIsContinueDisabled] = useState<boolean>(false),
    [ninStartPage, setNinStartPage] = useState<boolean>(
      !upgradeContext.isRecoveryFlow
    ),
    [ninStatus, setNinStatus] = useState<string | undefined>(ninInitialStatus),
    [ninValidationTrigger, setNinValidationTrigger] = useState<boolean>(false),
    [verifyingNin, setVerifyingNin] = useState<boolean>(
      !upgradeContext.isRecoveryFlow as boolean
    ),
    [formDataErrors, setFormDataErrors] = useState<FormErrors>({
      nationalIdentificationNumber: {
        message: "",
        hasError: false,
      },
    }),
    [formData, setFormData] = useState<FormDataFields>({
      nationalIdentificationNumber: "",
    });
  const { setOpen } = useNotificationContext();
  const [toasterData, setToasterData] = useState<{
    title: string;
    message: string;
    status: "success" | "error" | "warning";
  }>({
    title: "",
    message: "",
    status: "success",
  });

  function notify(
    title: string,
    message: string,
    status: "success" | "error" | "warning"
  ): void {
    setToasterData({
      title,
      message,
      status,
    });
    setOpen(true);
  }

  const onNationalIdentificationNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    let nationalIdentificationNumber = event.target.value as string;
    nationalIdentificationNumber = nationalIdentificationNumber.replace(
      /[^\d]/g,
      ""
    );
    setFormDataErrors({
      ...formDataErrors,
      nationalIdentificationNumber: { message: "", hasError: false },
    });
    setFormData({
      ...formData,
      nationalIdentificationNumber,
    });
  };

  const onFieldBlur = (
    field: keyof FormDataFields,
    validator: (field: string) => { message: string; hasError: boolean }
  ) => {
    setFormDataErrors({
      ...formDataErrors,
      [field]: validator(formData[field]),
    });
  };

  const handleContinueButton = () => {
    if (
      ninStatus === TierStepsResponseDTO.stepStatus.PENDING ||
      ninStatus === TierStepsResponseDTO.stepStatus.APPROVED ||
      ninStatus === NINVerificationResponseDTO.verificationStatus.PENDING ||
      ninStatus === NINVerificationResponseDTO.verificationStatus.SUCCESS
    ) {
      goToNextOpenStep();
    }
  };

  const handleModalState = () => {
    setBackModal(!backModal);
  };

  const handleDiscardUpgrade = () => {
    TierApiService.discardUpgradeTier()
      .then((response: ApiResponseDTODiscardUpgradeResponseDTO) => {
        if ("data" in response && response.data) {
          setActiveStep(0);
          clearUpgradeContext();
          navigateTo(UPGRADE_ACCOUNT);
        }
      })
      .catch((error) => {
        return error;
      });
  };

  const handleTryAgain = () => {
    setFormData({
      nationalIdentificationNumber: "",
    });
    setNinValidationTrigger(false);
    setVerifyingNin(true);
    setNinStatus(undefined);
    setNinStartPage(true);
    reset();
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    const request: NINVerificationRequestDTO = {
      nationalIdentificationNumber: formData.nationalIdentificationNumber,
    };
    setIsContinueDisabled(true);

    UsersApiService.postSubmitNinVerification(request)
      .then((response: ApiResponseDTONINVerificationResponseDTO) => {
        setIsContinueDisabled(false);
        setNinStartPage(false);
        setNinValidationTrigger(true);
      })
      .catch((error) => {
        const apiErrorMessage =
          error?.body?.message || CONNECTION_ERROR_MESSAGE;
        const title = getErrorTitle(error?.status);
        const message = apiErrorMessage;

        notify(title, message, "error");
        setIsContinueDisabled(false);
        throw new Error(error);
      });
  };

  // Use the specialized hook for NIN verification
  const { verificationStatus, polling, reset, resultText } =
    useNinVerificationPolling(
      ninValidationTrigger,
      verifyingNin,
      upgradeContext,
      setUpgradeContext
    );

  useEffect(() => {
    if (verificationStatus) {
      setNinStatus(verificationStatus);

      const isTerminalStatus =
        verificationStatus !==
          NINVerificationResponseDTO.verificationStatus.PENDING &&
        verificationStatus !== TierStepsResponseDTO.stepStatus.PENDING;

      if (isTerminalStatus) {
        setTimeout(() => {
          setVerifyingNin(false);
        }, 500);
      } else {
        setVerifyingNin(polling);
      }
    }
  }, [verificationStatus, polling]);

  useEffect(() => {
    if (
      ninStatus === TierStepsResponseDTO.stepStatus.PENDING ||
      ninStatus === NINVerificationResponseDTO.verificationStatus.PENDING
    ) {
      const currentStepIndex = upgradeContext.tierSteps?.findIndex(
        (step) =>
          step.stepName === TierStepsResponseDTO.stepName.NIN_VERIFICATION_STEP
      );

      if (
        currentStepIndex !== undefined &&
        currentStepIndex < (upgradeContext.tierSteps?.length || 0) - 1
      ) {
      }
    }
  }, [ninStatus, upgradeContext.tierSteps]);

  return (
    <div className="my-auto flex min-h-screen flex-col items-center justify-center">
      <ModalComponent
        body="Are you sure you want to leave?"
        secondLine="All information provided will be lost."
        title="Discard Upgrade"
        showModal={backModal}
        onClickClose={handleModalState}
        doubleOptions={true}
        onLeftButtonClick={handleDiscardUpgrade}
        onRightButtonClick={handleModalState}
        leftButtonText="Discard Upgrade"
        rightButtonText="Return to Upgrade"
      />
      {toasterData.message && toasterData.title && (
        <ToastSnackbarComponent
          horizontal="center"
          status={toasterData.status}
          vertical="top"
          hasAutoHideDuration
          size="lg"
          title={toasterData.title}
          text={toasterData.message}
        />
      )}

      {ninStartPage ? (
        <form onSubmit={handleSubmit}>
          <div>
            <div>
              <h1 className="mb-4 text-4.5xl font-medium text-neutral-900">
                NIN Verification
              </h1>
              <p className="mb-12 text-base font-normal text-neutral-600">
                Provide your National Identification Number
              </p>
            </div>
            <div>
              <TextFieldComponent
                id="nin-field"
                labelId="nin-field-label"
                containerClassName="flex grow"
                hasError={formDataErrors.nationalIdentificationNumber.hasError}
                label="National Identification Number (NIN/NIMC)"
                placeholder="Enter national identification number"
                type="text"
                value={formData.nationalIdentificationNumber}
                onChange={onNationalIdentificationNumberChange}
                onBlur={() =>
                  onFieldBlur(
                    "nationalIdentificationNumber",
                    setNationalIdentificationNumberValidations
                  )
                }
                maxLength={11}
              />
              {formDataErrors.nationalIdentificationNumber.hasError ? (
                <div className="ml-2 mt-1 text-xs text-red-400">
                  <span className="break-words">
                    {formDataErrors.nationalIdentificationNumber.message}
                  </span>
                </div>
              ) : (
                ""
              )}
            </div>
            <div className="mt-20 flex flex-row justify-between gap-4">
              <button
                id="nin-back-button"
                className="secondary-button"
                onClick={handleModalState}
                type="button"
              >
                Discard
              </button>
              <button
                id="nin-continue-button"
                className="primary-button"
                type="submit"
                disabled={
                  formData.nationalIdentificationNumber.length < 11 ||
                  isContinueDisabled
                }
              >
                Continue
              </button>
            </div>
          </div>
        </form>
      ) : (
        <div>
          {!verifyingNin &&
            (ninStatus ===
              NINVerificationResponseDTO.verificationStatus.PENDING ||
              ninStatus === TierStepsResponseDTO.stepStatus.PENDING) && (
              <div className="mt-6 flex flex-col">
                <h1 className="mb-16 text-4.5xl font-medium text-neutral-900">
                  NIN Verification
                </h1>
                <div className="ml-2 flex flex-row items-center">
                  <Clock transform="scale(1.70)" />
                  <h1 className="ml-4 text-2xl font-normal text-neutral-900">
                    Waiting For Approval
                  </h1>
                </div>
                <p className="ml-10 text-sm font-normal text-neutral-600">
                  Press "Continue" to proceed.
                </p>
                <div className="stepper-buttons-container !mt-20">
                  <button
                    id="back-button"
                    className="secondary-button"
                    onClick={handleModalState}
                    type="button"
                  >
                    Discard
                  </button>
                  <button
                    id="continue-button"
                    className="primary-button"
                    onClick={handleContinueButton}
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}
          {!verifyingNin &&
            (ninStatus ===
              NINVerificationResponseDTO.verificationStatus.SUCCESS ||
              ninStatus === TierStepsResponseDTO.stepStatus.APPROVED) && (
              <div className="mt-6 flex flex-col">
                <h1 className="mb-16 text-4.5xl font-medium text-neutral-900">
                  NIN Verification
                </h1>
                <div className="ml-2 flex flex-row items-center">
                  <CheckCircle transform="scale(1.70)" />
                  <h1 className="text-neutral-850 ml-4 text-2xl font-normal">
                    Information Successfully Verified
                  </h1>
                </div>
                <p className="ml-14 text-sm font-normal text-neutral-600">
                  Press "Continue" to proceed.
                </p>
                <div className="stepper-buttons-container !mt-20">
                  <button
                    id="back-button"
                    className="secondary-button"
                    onClick={handleModalState}
                    type="button"
                  >
                    Discard
                  </button>
                  <button
                    id="continue-button"
                    className="primary-button"
                    onClick={handleContinueButton}
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}
          {!verifyingNin &&
            (ninStatus ===
              NINVerificationResponseDTO.verificationStatus.FAILED ||
              ninStatus === TierStepsResponseDTO.stepStatus.REJECTED) && (
              <div className="mt-6 flex flex-col">
                <h1 className="mb-16 text-4.5xl font-medium text-neutral-900">
                  NIN Verification
                </h1>
                <div className="ml-2 flex flex-row items-center">
                  <CloseCircle transform="scale(1.70)" />
                  <h1 className="ml-4 text-2xl font-normal text-neutral-900">
                    Information Verification Failed
                  </h1>
                </div>

                {resultText && (
                  <p className="ml-14 mt-2 text-sm font-normal text-red-600">
                    {resultText}
                  </p>
                )}
                <div className="stepper-buttons-container !mt-20">
                  <button
                    id="back-button"
                    className="secondary-button"
                    onClick={handleModalState}
                    type="button"
                  >
                    Discard
                  </button>
                  <button
                    id="continue-button"
                    className="primary-button"
                    onClick={handleTryAgain}
                  >
                    Try again
                  </button>
                </div>
              </div>
            )}
          {verifyingNin && (
            <div className="mt-20 lg:ml-10 lg:flex">
              <div className="flex flex-col items-center">
                <h1 className="mb-10 text-4.5xl font-medium text-neutral-900">
                  NIN Verification
                </h1>
                <SpinnerComponent size={64} type={"primary"} showIcon={false} />
                <p className="mt-6 text-base font-normal text-neutral-600">
                  Your information is being verified.
                </p>
                <p className="text-sm font-normal text-neutral-400">
                  This should not take too long.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default NinVerificationPage;
